import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faSync } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import SubtleBadge from "components/common/SubtleBadge";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import paths from "routes/paths";
import { errorMessage, getOverdueAudits } from "./auditApi";

/**
 * Overdue-audit report (INVUI-707). Doubles as the patient list on the
 * Inventory page: each name links to that patient's inventory page
 * (/inventory/:id), the same way View Patient links to patient details.
 */

const formatDate = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("MM/DD/YY") : String(value);
};

const daysOverdueOf = (row) => {
  if (row.daysOverdue !== null && row.daysOverdue !== undefined) return row.daysOverdue;
  if (!row.dueDate) return null;
  const due = moment(row.dueDate);
  return due.isValid() ? Math.max(0, moment().startOf("day").diff(due.startOf("day"), "days")) : null;
};

// Header and cells share one alignment per column, like the Order History
// table: names left, short values centred.
const ALIGN_LEFT = {
  headerProps: { className: "text-900 text-start" },
  cellProps: { className: "text-start" }
};
const ALIGN_CENTER = {
  headerProps: { className: "text-900 text-center" },
  cellProps: { className: "text-center" }
};

const OverdueAudits = ({ facilityId, facilityName, groupId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await getOverdueAudits({ facilityId, groupId });
      setRows(
        list
          .map((r) => ({ ...r, days: daysOverdueOf(r) }))
          .sort((a, b) => (b.days ?? -1) - (a.days ?? -1))
      );
    } catch (err) {
      setError(errorMessage(err, "The overdue audit report could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [facilityId, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const showFacility = !facilityId;

  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "patientName",
        header: "Patient",
        meta: ALIGN_LEFT,
        cell: ({ row: { original } }) => (
          // Name and facility ride along so the page can show them straight away.
          <Link
            to={{ pathname: `${paths.inventory}/${original.patientId}` }}
            state={{ patientName: original.patientName, facilityName: original.facilityName }}
            className="fw-semibold text-decoration-none"
            title="Open this patient's inventory"
          >
            {original.patientName}
          </Link>
        )
      }
    ];
    if (showFacility) {
      cols.push({
        accessorKey: "facilityName",
        header: "Facility",
        meta: ALIGN_LEFT
      });
    }
    cols.push(
      {
        id: "dueDate",
        accessorFn: (r) => formatDate(r.dueDate),
        header: "Due",
        meta: ALIGN_CENTER
      },
      {
        id: "days",
        accessorFn: (r) => r.days ?? -1,
        header: "Overdue by",
        meta: ALIGN_CENTER,
        cell: ({ row: { original } }) =>
          original.days === null || original.days === undefined ? (
            <span className="text-500">—</span>
          ) : (
            <SubtleBadge
              bg={original.alwaysSkippable ? "warning" : "danger"}
              pill
              className="d-inline-block"
            >
              {original.days} day{original.days === 1 ? "" : "s"}
            </SubtleBadge>
          )
      },
      {
        id: "lastAuditAt",
        accessorFn: (r) => formatDate(r.lastAuditAt),
        header: "Last audit",
        meta: ALIGN_CENTER
      },
      {
        id: "kind",
        accessorFn: (r) => (r.alwaysSkippable ? "Reminder only" : "Needs doing"),
        header: "Type",
        meta: ALIGN_CENTER,
        cell: ({ row: { original } }) => (
          <SubtleBadge
            bg={original.alwaysSkippable ? "secondary" : "danger"}
            pill
            className="d-inline-block"
          >
            {original.alwaysSkippable ? "Reminder only" : "Needs doing"}
          </SubtleBadge>
        )
      }
    );
    return cols;
  }, [showFacility]);

  const table = useAdvanceTable({
    data: rows,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="d-flex gap-2 mb-3" style={{ maxWidth: 360 }}>
        <AdvanceTableSearchBox placeholder="Search patients..." />
        {/* <Button
          variant="outline-secondary"
          size="sm"
          className="border-300 flex-shrink-0"
          onClick={load}
          disabled={loading}
          title="Refresh"
        >
          <FontAwesomeIcon icon={faSync} spin={loading} />
        </Button> */}
      </div>

      {loading && rows.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <p className="mt-3 text-600 fs-10 mb-0">Loading overdue audits...</p>
        </div>
      ) : error ? (
        <Alert variant="danger" className="d-flex align-items-start border-0 rounded-3 fs-10">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
          <div className="flex-1">
            <div className="fw-bold">We couldn&apos;t load the overdue audits</div>
            <div>{error}</div>
          </div>
          <Button variant="outline-danger" size="sm" className="ms-3 rounded-pill px-3" onClick={load}>
            Retry
          </Button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="text-center py-5 px-3 border border-300 border-dashed rounded-3">
          <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-5 mb-3" />
          <h6 className="text-900 fw-bold mb-1 fs-9">No overdue audits</h6>
          <p className="text-600 fs-10 mb-0">
            Every patient{facilityName ? " in this facility" : ""} is up to date.
          </p>
        </div>
      ) : (
        <>
          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle text-center"
            rowClassName="text-nowrap align-middle"
            tableProps={{ size: "lg", striped: true, className: "fs-10 mb-0" }}
          />
          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </>
      )}
    </AdvanceTableProvider>
  );
};

OverdueAudits.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityName: PropTypes.string,
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default OverdueAudits;
